// app/javascript/controllers/parcel_editor_controller.js
// Stimulus controller for the Mapbox-powered parcel drawing + splitting UI
// Dependencies: mapbox-gl, @mapbox/mapbox-gl-draw, @turf/turf

import { Controller } from "@hotwired/stimulus"
import mapboxgl from "mapbox-gl"
import MapboxDraw from "@mapbox/mapbox-gl-draw"
import * as turf from "@turf/turf"
import { formatArea } from "../lib/intl_format"

export default class extends Controller {
  static targets = [
    "map",
    "geometryInput",    // hidden input storing final GeoJSON
    "areaDisplay",      // shows computed area (viewer-localised)
    "splitPanel",       // shown when split mode is active
    "splitNameA",
    "splitNameB",
    "saveBtn"
  ]

  static values = {
    accessToken: String,
    existingGeojson: String,  // parcel polygon to load on edit
    mode: String,             // "parcel" | "sub_parcel" | "split"
    splitTargetId: Number,    // sub_parcel id being split
    country: String           // ISO 3166-1 alpha-2 — selects cadastral provider
  }

  connect() {
    mapboxgl.accessToken = this.accessTokenValue

    this.map = new mapboxgl.Map({
      container: this.mapTarget,
      style: "mapbox://styles/mapbox/satellite-streets-v12",
      center: [10, 20],  // world view — geolocate control or existing parcel takes over
      zoom: 1.5
    })

    this.draw = new MapboxDraw({
      displayControlsDefault: false,
      controls: {
        polygon: true,
        trash:   true
      },
      defaultMode: "draw_polygon"
    })

    this.map.addControl(this.draw)
    this.map.addControl(new mapboxgl.NavigationControl())
    this.map.addControl(new mapboxgl.GeolocateControl({ trackUserLocation: false }))

    this.map.on("load", () => {
      this.loadExistingGeometry()
      this.loadCadastralParcels()
    })

    this.map.on("draw.create", () => this.onDrawUpdate())
    this.map.on("draw.update", () => this.onDrawUpdate())
    this.map.on("draw.delete", () => this.onDrawUpdate())
  }

  // ── Drawing ──────────────────────────────────────────────────────────────

  onDrawUpdate() {
    const data = this.draw.getAll()
    if (!data.features.length) {
      this.geometryInputTarget.value = ""
      if (this.hasAreaDisplayTarget) this.areaDisplayTarget.textContent = "–"
      return
    }

    const feature = data.features[0]
    const geojson = JSON.stringify(feature.geometry)
    this.geometryInputTarget.value = geojson

    // turf.area returns m² — already canonical, just format for the viewer
    if (this.hasAreaDisplayTarget) {
      this.areaDisplayTarget.textContent = formatArea(turf.area(feature))
    }
  }

  loadExistingGeometry() {
    if (!this.existingGeojsonValue) return

    try {
      const geom = JSON.parse(this.existingGeojsonValue)
      const feature = { type: "Feature", geometry: geom, properties: {} }
      this.draw.add(feature)

      // Fly to it
      const bbox = turf.bbox(feature)
      this.map.fitBounds(bbox, { padding: 60 })
    } catch (e) {
      console.error("Failed to load existing geometry", e)
    }
  }

  // ── Cadastral parcel overlay ──────────────────────────────────────────────
  // Official boundaries from the local cadastre, when the country has a
  // provider (see Cadastral::Providers). Empty response = draw by hand.

  async loadCadastralParcels() {
    const center  = this.map.getCenter()
    const country = this.countryValue || ""
    try {
      const res  = await fetch(`/map/cadastral_parcels.json?lat=${center.lat}&lng=${center.lng}&country=${country}`)
      const data = await res.json()

      if (!data.features?.length) return

      this.map.addSource("cadastral-parcels", { type: "geojson", data })

      this.map.addLayer({
        id:     "cadastral-parcels-fill",
        type:   "fill",
        source: "cadastral-parcels",
        paint:  {
          "fill-color":   "#f59e0b",
          "fill-opacity": 0.15
        }
      })

      this.map.addLayer({
        id:     "cadastral-parcels-outline",
        type:   "line",
        source: "cadastral-parcels",
        paint:  {
          "line-color": "#f59e0b",
          "line-width": 1.5
        }
      })

      // Click to import an official parcel boundary
      this.map.on("click", "cadastral-parcels-fill", (e) => {
        const feature = e.features[0]
        if (!feature) return

        if (confirm(`Import this parcel boundary (${formatArea(turf.area(feature))})?`)) {
          this.draw.deleteAll()
          this.draw.add(feature)
          this.onDrawUpdate()
        }
      })

      this.map.on("mouseenter", "cadastral-parcels-fill", () => {
        this.map.getCanvas().style.cursor = "pointer"
      })
      this.map.on("mouseleave", "cadastral-parcels-fill", () => {
        this.map.getCanvas().style.cursor = ""
      })

    } catch (e) {
      console.warn("Cadastral parcels unavailable", e)
    }
  }

  // ── Parcel splitting ──────────────────────────────────────────────────────

  // Called when user clicks "Split this section" on a sub_parcel
  enterSplitMode(event) {
    const subParcelGeojson = event.currentTarget.dataset.geojson

    this.splitSourcePolygon = JSON.parse(subParcelGeojson)
    this.splitPanelTarget.classList.remove("hidden")

    // Load the sub_parcel polygon as a non-editable reference layer
    if (this.map.getSource("split-target")) {
      this.map.getSource("split-target").setData({
        type: "Feature",
        geometry: this.splitSourcePolygon
      })
    } else {
      this.map.addSource("split-target", {
        type: "geojson",
        data: { type: "Feature", geometry: this.splitSourcePolygon }
      })
      this.map.addLayer({
        id: "split-target-fill",
        type: "fill",
        source: "split-target",
        paint: { "fill-color": "#10b981", "fill-opacity": 0.2 }
      })
      this.map.addLayer({
        id: "split-target-outline",
        type: "line",
        source: "split-target",
        paint: { "line-color": "#10b981", "line-width": 2 }
      })
    }

    const bbox = turf.bbox({ type: "Feature", geometry: this.splitSourcePolygon })
    this.map.fitBounds(bbox, { padding: 80 })

    // Switch draw to line_string for the cut line
    this.draw.changeMode("draw_line_string")

    this.map.once("draw.create", (e) => this.performSplit(e.features[0]))
  }

  performSplit(cutLineFeature) {
    const polygon = turf.polygon(this.splitSourcePolygon.coordinates)
    const cutLine = cutLineFeature

    // Use turf.lineSplit to split the polygon
    const splitResult = turf.lineSplit(polygon, cutLine)

    if (splitResult.features.length !== 2) {
      alert("The cut line must cross the field boundary twice to split it cleanly. Try again.")
      this.draw.deleteAll()
      this.draw.changeMode("draw_line_string")
      this.map.once("draw.create", (e) => this.performSplit(e.features[0]))
      return
    }

    const [polyA, polyB] = splitResult.features

    // Show preview of the two pieces
    this.showSplitPreview(polyA, polyB)
  }

  showSplitPreview(polyA, polyB) {
    // Update the panel with each piece's area (m² from turf, localised display)
    this.splitPanelTarget.querySelector("[data-area-a]").textContent = formatArea(turf.area(polyA))
    this.splitPanelTarget.querySelector("[data-area-b]").textContent = formatArea(turf.area(polyB))

    // Store for submission
    this._splitPolyA = polyA
    this._splitPolyB = polyB

    // Render preview layers
    ["split-preview-a", "split-preview-b"].forEach(id => {
      if (this.map.getSource(id)) this.map.removeLayer(id + "-fill")
      if (this.map.getSource(id)) this.map.removeSource(id)
    })

    this.map.addSource("split-preview-a", { type: "geojson", data: polyA })
    this.map.addLayer({
      id: "split-preview-a-fill", type: "fill",
      source: "split-preview-a",
      paint: { "fill-color": "#3b82f6", "fill-opacity": 0.4 }
    })

    this.map.addSource("split-preview-b", { type: "geojson", data: polyB })
    this.map.addLayer({
      id: "split-preview-b-fill", type: "fill",
      source: "split-preview-b",
      paint: { "fill-color": "#f97316", "fill-opacity": 0.4 }
    })
  }

  async confirmSplit() {
    if (!this._splitPolyA || !this._splitPolyB) return

    const nameA = this.splitNameATarget.value
    const nameB = this.splitNameBTarget.value
    const subParcelId = this.splitTargetIdValue

    const res = await fetch(`/owner/sub_parcels/${subParcelId}/split`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name=csrf-token]").content
      },
      body: JSON.stringify({
        result_a: JSON.stringify(this._splitPolyA.geometry),
        result_b: JSON.stringify(this._splitPolyB.geometry),
        name_a:   nameA,
        name_b:   nameB
      })
    })

    if (res.ok) {
      window.location.reload()
    } else {
      const err = await res.json()
      alert(err.error || "Split failed")
    }
  }

  cancelSplit() {
    this.draw.deleteAll()
    this.splitPanelTarget.classList.add("hidden")
    this._splitPolyA = null
    this._splitPolyB = null
  }

  disconnect() {
    this.map?.remove()
  }
}
